import {
  FUNNEL_BEFORE_FIRST_STAGE,
  type FunnelAggregates,
} from "@repo/app-types/api/funnel";
import type {
  FunnelId,
  FunnelMoments,
  FunnelStages,
} from "@repo/app-types/entities/funnel";
import {
  buildOutcomeGreenRateLabel,
  getOutcomePluralNoun,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import { InfoIconTooltip } from "@repo/ui/tooltip/infoIconTooltip";
import { useMemo, useState, type FC } from "react";
import { Heading } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import {
  buildFunnelMomentRows,
  checkFunnelLiftShown,
  computeFunnelMomentShare,
  computeFunnelMomentStageShares,
  countFunnelApplicableInteractions,
  formatFunnelLift,
  formatFunnelPoints,
  formatFunnelShare,
  formatFunnelStageInteractions,
  formatFunnelStageName,
  type FunnelMomentCohortCounts,
  type FunnelMomentRow,
  type FunnelMomentStageShare,
  type FunnelStageFocus,
} from "./funnelFormatting.js";
import { FunnelMomentShareBar } from "./funnelMomentShareBar.js";
import {
  FunnelMomentAskButton,
  FunnelMomentSlideover,
} from "./funnelMomentSlideover.js";

export interface FunnelMomentListProps {
  funnelId: FunnelId;
  /** The voice agent whose numbers these are; absent on a playbook's funnel. */
  teammateAgentId?: TeammateId;
  stages: FunnelStages;
  moments: FunnelMoments;
  aggregates: FunnelAggregates;
  stageKey: FunnelStageFocus;
  /** Names what a success is; null when the subject's playbooks share no outcome. */
  outcomeDefinition: OutcomeDefinition | null;
}

/** The subject's moments in the focused stage, or across all stages. */
export const FunnelMomentList: FC<FunnelMomentListProps> = (props) => {
  const {
    funnelId,
    teammateAgentId,
    stages,
    moments,
    aggregates,
    stageKey,
    outcomeDefinition,
  } = props;
  const successNoun = outcomeDefinition
    ? lowercaseFirstLetter(getOutcomePluralNoun(outcomeDefinition))
    : "successes";
  const rateLabel = outcomeDefinition
    ? lowercaseFirstLetter(buildOutcomeGreenRateLabel(outcomeDefinition))
    : "success rate";
  const isAllStages = stageKey === undefined;
  const stageDescription =
    stageKey === undefined || stageKey === null
      ? undefined
      : stages.find((stage) => stage.key === stageKey)?.description;

  const agentCounts = useMemo(
    (): FunnelMomentCohortCounts => ({
      momentCounts: aggregates.moments.agent,
      momentTotals: aggregates.moment_totals.agent,
      stageCounts: aggregates.stages.agent,
    }),
    [aggregates],
  );
  const topRepCounts = useMemo(
    (): FunnelMomentCohortCounts => ({
      momentCounts: aggregates.moments.top_rep,
      momentTotals: aggregates.moment_totals.top_rep,
      stageCounts: aggregates.stages.top_rep,
    }),
    [aggregates],
  );
  const rows = useMemo(
    () =>
      buildFunnelMomentRows({
        stages,
        moments,
        counts: agentCounts,
        impacts: aggregates.impact.agent,
        stageKey,
      }),
    [stages, moments, agentCounts, aggregates, stageKey],
  );
  const applicableInteractions = countFunnelApplicableInteractions(
    aggregates.stages.agent,
  );

  return (
    <div className="space-y-5 border-t border-border-light pt-4">
      <div className="space-y-1">
        <Heading level={3} className="text-base font-semibold text-foreground">
          {stageKey === undefined
            ? "Moments across all stages"
            : stageKey === null
              ? `Moments before ‘${stages[0]?.name ?? "any stage"}’`
              : `Moments in ‘${formatFunnelStageName(stages, stageKey)}’`}
        </Heading>
        {stageDescription ? (
          <p className="text-sm text-muted-foreground">{stageDescription}</p>
        ) : null}
      </div>
      <div aria-live="polite">
        {rows.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-baseline gap-4 px-2">
              <Heading
                level={4}
                className="min-w-0 flex-1 text-xs text-muted-foreground"
              >
                Moments
              </Heading>
              {isAllStages && (
                <span className="hidden w-24 shrink-0 text-left text-xs text-muted-foreground md:block">
                  Stages
                </span>
              )}
              <span className="w-44 shrink-0 text-left text-xs text-muted-foreground sm:w-64">
                Interactions with this moment
              </span>
              <span className="flex w-24 shrink-0 items-center justify-end gap-1 text-xs text-muted-foreground">
                Potential lift
                <InfoIconTooltip>
                  <div className="max-w-64 space-y-2">
                    <p>
                      Estimated number of {successNoun} and {rateLabel}{" "}
                      percentage points impacted by how each moment was handled.
                    </p>
                    <p>
                      * Low confidence estimate based on a small number of
                      interactions.
                    </p>
                  </div>
                </InfoIconTooltip>
              </span>
            </div>
            <ul className="divide-y divide-border-light">
              {rows.map((row) => (
                <li key={row.momentKey}>
                  <FunnelMomentListRow
                    funnelId={funnelId}
                    teammateAgentId={teammateAgentId}
                    stages={stages}
                    stageKey={stageKey}
                    row={row}
                    stageShares={
                      isAllStages
                        ? computeFunnelMomentStageShares({
                            stages,
                            counts: agentCounts,
                            momentKey: row.momentKey,
                          })
                        : null
                    }
                    applicableInteractions={applicableInteractions}
                    topRepShare={computeFunnelMomentShare({
                      counts: topRepCounts,
                      momentKey: row.momentKey,
                      stageKey,
                    })}
                  />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {isAllStages ? "No moments yet" : "No moments in this stage"}
          </p>
        )}
      </div>
    </div>
  );
};

interface FunnelMomentListRowProps {
  funnelId: FunnelId;
  teammateAgentId?: TeammateId;
  stages: FunnelStages;
  stageKey: FunnelStageFocus;
  row: FunnelMomentRow;
  /** Across all stages, each stage's share with this moment; else null. */
  stageShares: FunnelMomentStageShare[] | null;
  /** Interactions that can succeed, for the lift in points. */
  applicableInteractions: number;
  /** The top reps' share of the stage with this moment; null without any. */
  topRepShare: number | null;
}

const FunnelMomentListRow: FC<FunnelMomentListRowProps> = (props) => {
  const { stages, stageKey, row, stageShares, applicableInteractions } = props;
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <div className="group relative rounded-md focus-within:bg-muted hover:bg-muted">
        <Button
          variant={ButtonVariant.Tertiary}
          className="absolute inset-0 h-full w-full rounded-md focus-visible:outline-2 focus-visible:outline-primary"
          onClick={() => setIsOpen(true)}
        >
          <span className="sr-only">View exchanges for {row.momentName}</span>
        </Button>
        <div className="pointer-events-none relative flex items-center gap-4 px-2 py-3 text-sm">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span>{row.momentName}</span>
            <FunnelMomentAskButton
              isVoiceAgent={props.teammateAgentId !== undefined}
              momentName={row.momentName}
              stages={stages}
              stageKey={stageKey}
              className="pointer-events-auto -my-1 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
            />
          </div>
          {stageShares && (
            <FunnelMomentStageHeatMap
              momentName={row.momentName}
              stageShares={stageShares}
            />
          )}
          <FunnelMomentShareBar
            momentName={row.momentName}
            share={row.share}
            parts={row.parts}
            stageInteractions={formatFunnelStageInteractions(stages, stageKey)}
          />
          <FunnelMomentLift
            row={row}
            applicableInteractions={applicableInteractions}
          />
        </div>
      </div>
      <SlideoverContainer
        size={Size.LARGE}
        isDismissable
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      >
        <FunnelMomentSlideover {...props} onAsk={() => setIsOpen(false)} />
      </SlideoverContainer>
    </>
  );
};

/** Shades from the moment's least to its most common stage, written out so Tailwind sees them. */
const heatMapClassNames = [
  "bg-indigo-100",
  "bg-indigo-200",
  "bg-indigo-300",
  "bg-indigo-400",
  "bg-indigo-500",
] as const;

interface FunnelMomentStageHeatMapProps {
  momentName: string;
  stageShares: readonly FunnelMomentStageShare[];
}

/** One segment per stage, darker where more of the stage's interactions had the moment. */
const FunnelMomentStageHeatMap: FC<FunnelMomentStageHeatMapProps> = (props) => {
  const { momentName, stageShares } = props;
  const maxShare = Math.max(0, ...stageShares.map((stage) => stage.share));
  const description = stageShares
    .map((stage) => `${stage.stageName} ${formatFunnelShare(stage.share)}`)
    .join(", ");

  return (
    <span
      role="img"
      aria-label={`${momentName} by stage: ${description}`}
      className="hidden h-1.5 w-24 shrink-0 gap-0.5 md:flex"
    >
      {stageShares.map((stage) => (
        <span
          key={stage.stageKey ?? FUNNEL_BEFORE_FIRST_STAGE}
          className={twMerge(
            "h-full min-w-0 flex-1 rounded-xs",
            stage.share > 0 && maxShare > 0
              ? heatMapClassNames[
                  Math.min(
                    heatMapClassNames.length - 1,
                    Math.floor(
                      (stage.share / maxShare) * heatMapClassNames.length,
                    ),
                  )
                ]
              : "bg-border-strong/40",
          )}
        />
      ))}
    </span>
  );
};

interface FunnelMomentLiftProps {
  row: FunnelMomentRow;
  applicableInteractions: number;
}

const FunnelMomentLift: FC<FunnelMomentLiftProps> = (props) => {
  const { row, applicableInteractions } = props;
  const successes = row.impact?.successes_lost ?? 0;
  const isShown = checkFunnelLiftShown(successes);

  return (
    <span className="flex w-24 shrink-0 flex-col items-end text-xs tabular-nums">
      <span className="relative text-foreground">
        {formatFunnelLift(successes)}
        {row.impact?.has_few_interactions && isShown && (
          // Outside the flow, so the numbers stay aligned.
          <span className="absolute left-full pl-0.5 text-muted-foreground">
            <span aria-hidden="true">*</span>
            <span className="sr-only">, seen in few interactions</span>
          </span>
        )}
      </span>
      {isShown && applicableInteractions > 0 && (
        <span className="text-muted-foreground">
          {formatFunnelPoints(successes / applicableInteractions)}
        </span>
      )}
    </span>
  );
};

function lowercaseFirstLetter(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}
